import AboutDetail from "./UI/AboutDetail";
import { ReactComponent as StarShooting } from "../img/star-shooting.svg";
import { ReactComponent as Tools } from "../img/tools.svg";
import { ReactComponent as Light } from "../img/light.svg";
import Title from "./UI/Title";
import PageContent from "./UI/PageContent";

const HIGHLIGHTS = [
  { value: "6+", label: "Years in startups & scaleups" },
  { value: "Fullstack", label: "Web & mobile products" },
  { value: "AI Augmented", label: "Integrated in daily workflows" },
  { value: "Full Remote", label: "Normandy, France" },
];

const INTRO_BLOCKS = [
  {
    emphasis: "fullstack developer",
    text: "I'm a self-taught fullstack developer with over 6 years of experience in French startups and scaleups. After several years of learning through online courses, I joined a programming school in 2021 to sharpen my skills.",
  },
  {
    emphasis: "entire product lifecycle",
    text: "I've contributed across the entire product lifecycle, from understanding business needs to building and iterating on production-ready web applications.",
  },
  {
    emphasis: "AI into my workflows",
    text: "I now actively integrate AI into my workflows to accelerate delivery while maintaining a high standard of quality.",
  },
];

const FEATURE_CARDS = [
  {
    icon: <StarShooting className="w-5 h-5" />,
    title: "Skills",
    items: [
      "Fullstack web development",
      "Mobile app development",
      "Clean Architecture",
      "API design & integration",
      "UI/UX design",
    ],
  },
  {
    icon: <Tools className="w-5 h-5" />,
    title: "Tools",
    items: [
      { label: "Frontend", subItems: "Typescript, React, Next.js, React Native, BFF..." },
      { label: "Backend", subItems: "C#, .Net, Graphql, REST API, Kafka..." },
      { label: "Database", subItems: "SQL, PostgresSQL, MongoDB, Google Cloud..." },
      { label: "Code quality", subItems: "DDD, Clean Code, Hexagonale Architecture..." },
    ],
  },
  {
    icon: <Light className="w-5 h-5" />,
    title: "Expertise",
    items: [
      "Startups & scaling environment",
      "Project & delivery management",
      "Product development",
      "AI-assisted development",
      "Cross-functional leadership",
    ],
  },
];

function About() {
  return (
    <PageContent>
      <div className="section-stack">
        <Title title="✨ About me" />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
          {HIGHLIGHTS.map((item) => (
            <div
              key={item.label}
              className="glass-card px-4 py-4 text-center sm:text-left"
            >
              <p className="text-lg md:text-xl font-bold gradient-text-purple">{item.value}</p>
              <p className="text-xs md:text-sm text-muted mt-1 leading-snug">{item.label}</p>
            </div>
          ))}
        </div>

        <div>
          <div className="glass-card p-6 md:p-8 flex flex-col gap-6">
            {INTRO_BLOCKS.map((block, index) => (
              <div
                key={block.emphasis}
                className={`flex gap-4 ${index > 0 ? "pt-6 border-t card-divider" : ""}`}
              >
                <span
                  className="shrink-0 w-1 rounded-full bg-gradient-to-b from-[#3D2C8D] to-[#7C3AED]"
                  aria-hidden
                />
                <p className="text-base md:text-lg leading-relaxed text-muted">
                  {renderIntroText(block.text, block.emphasis)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {FEATURE_CARDS.map((card) => (
            <FeatureCard key={card.title} icon={card.icon} title={card.title} items={card.items} />
          ))}
        </div>
      </div>
    </PageContent>
  );
}

export default About;

function renderIntroText(text, emphasis) {
  const parts = text.split(emphasis);
  if (parts.length === 1) return text;

  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <span className="font-semibold text-ink">{emphasis}</span>
      )}
    </span>
  ));
}

function FeatureCard({ icon, title, items }) {
  return (
    <div className="glass-card px-5 py-5 h-80 flex flex-col">
      <div className="flex items-center justify-center gap-2.5 mb-4 shrink-0">
        {icon}
        <h3 className="font-bold text-lg text-ink">{title}</h3>
      </div>

      <div className="flex flex-col gap-3 text-sm leading-snug flex-1 min-h-0 overflow-y-auto">
        {items.map((item) =>
          typeof item === "string" ? (
            <p key={item} className="font-semibold text-ink">
              {item}
            </p>
          ) : (
            <p key={item.label} className="text-ink">
              <span className="font-semibold">{item.label}</span>
              <span className="text-muted"> — {item.subItems}</span>
            </p>
          )
        )}
      </div>
    </div>
  );
}
